'use client';

import { useAddressSearch } from '@/hooks/useAddressSearch';
import type { AddressSuggestion, ParsedAddress } from '@/lib/address/types';
import clsx from 'clsx';
import { useCallback, useEffect, useRef, useState } from 'react';
import styles from './AddressAutocomplete.module.scss';

export interface AddressAutocompleteProps {
  /** Current input value */
  value: string;
  /** Callback when input changes */
  onChange: (value: string) => void;
  /** Callback when an address is selected */
  onSelect: (address: ParsedAddress) => void;
  /** Optional callback with the full Melissa suggestion */
  onSelectSuggestion?: (suggestion: AddressSuggestion) => void;
  /** Country code for address search */
  countryCode?: string;
  /** Placeholder text */
  placeholder?: string;
  /** Error message to display */
  error?: string;
  /** Label text */
  label?: string;
  /** Secondary hint beside the label (e.g. PO box restriction) */
  hint?: string;
  /** Whether the field is required */
  required?: boolean;
  /** Additional class name */
  className?: string;
  /** Input name attribute */
  name?: string;
  /** Input id attribute */
  id?: string;
  /** Disable autocomplete functionality */
  disabled?: boolean;
  /** Input test id */
  dataTestId?: string;
  /** Accessible label for the suggestions listbox */
  listboxAriaLabel?: string;
  /** Loading copy shown in the dropdown */
  loadingLabel?: string;
  /** Empty-state copy when search completes with no matches (query length ≥ 3) */
  emptyLabel?: string;
  /** Max Melissa records to request */
  maxRecords?: number;
  /** Which suggestion value to write back into the input on select */
  fillInputOnSelect?: 'streetAddress1' | 'displayText';
}

/**
 * Shared Melissa address autocomplete with a custom combobox dropdown.
 */
export function AddressAutocomplete({
  value,
  onChange,
  onSelect,
  onSelectSuggestion,
  countryCode = 'US',
  placeholder = 'Start typing your address...',
  error,
  label,
  hint,
  required,
  className,
  name,
  id,
  disabled,
  dataTestId,
  listboxAriaLabel,
  loadingLabel = 'Searching addresses…',
  emptyLabel,
  maxRecords = 8,
  fillInputOnSelect = 'streetAddress1',
}: Readonly<AddressAutocompleteProps>) {
  const MIN_QUERY_LENGTH = 3;
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const { suggestions, isLoading, search, clear } = useAddressSearch({
    countryCode,
    maxRecords,
  });

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      onChange(newValue);
      search(newValue);
      setIsOpen(true);
      setHighlightedIndex(-1);
    },
    [onChange, search]
  );

  const handleSelect = useCallback(
    (suggestion: AddressSuggestion) => {
      const nextValue =
        fillInputOnSelect === 'displayText'
          ? suggestion.displayText
          : suggestion.address.streetAddress1;
      onChange(nextValue);
      onSelect(suggestion.address);
      onSelectSuggestion?.(suggestion);
      setIsOpen(false);
      clear();
    },
    [onChange, onSelect, onSelectSuggestion, clear, fillInputOnSelect]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!isOpen || suggestions.length === 0) {
        return;
      }

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlightedIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : 0));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : suggestions.length - 1));
          break;
        case 'Enter':
          e.preventDefault();
          if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
            handleSelect(suggestions[highlightedIndex]);
          }
          break;
        case 'Escape':
          setIsOpen(false);
          clear();
          break;
      }
    },
    [isOpen, suggestions, highlightedIndex, handleSelect, clear]
  );

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const items = listRef.current.querySelectorAll('[role="option"]');
      const item = items[highlightedIndex];
      if (item) {
        item.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex]);

  const showEmptyState =
    !!emptyLabel && value.length >= MIN_QUERY_LENGTH && !isLoading && suggestions.length === 0;
  const showDropdown = isOpen && (suggestions.length > 0 || isLoading || showEmptyState);
  const inputId = id || name || 'address-autocomplete';
  const showError = !!error;

  let dropdownListContent;
  if (isLoading && suggestions.length === 0) {
    dropdownListContent = <li className={styles.loadingItem}>{loadingLabel}</li>;
  } else if (showEmptyState) {
    dropdownListContent = <li className={styles.emptyItem}>{emptyLabel}</li>;
  } else {
    dropdownListContent = suggestions.map((suggestion, index) => (
      <li
        key={suggestion.id}
        id={`${inputId}-option-${index}`}
        role="option"
        aria-selected={index === highlightedIndex}
        className={clsx(styles.option, {
          [styles.highlighted]: index === highlightedIndex,
        })}
        onClick={() => handleSelect(suggestion)}
        onMouseEnter={() => setHighlightedIndex(index)}
      >
        <span className={styles.optionText}>{suggestion.displayText}</span>
      </li>
    ));
  }

  return (
    <div
      ref={containerRef}
      className={clsx(styles.container, { [styles.hasError]: showError }, className)}
    >
      {label && (
        <div className={styles.labelRow}>
          <label htmlFor={inputId} className={styles.title}>
            {label}
            {required && (
              <span className={styles.required} aria-hidden="true">
                *
              </span>
            )}
          </label>
          {hint ? <span className={styles.hint}>{hint}</span> : null}
        </div>
      )}

      <div className={styles.inputWrapper}>
        <input
          ref={inputRef}
          type="text"
          id={inputId}
          name={name}
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (suggestions.length > 0 || isLoading || showEmptyState) {
              setIsOpen(true);
            }
          }}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={showDropdown}
          aria-controls={`${inputId}-listbox`}
          aria-activedescendant={
            highlightedIndex >= 0 ? `${inputId}-option-${highlightedIndex}` : undefined
          }
          aria-invalid={showError}
          className={styles.input}
          data-testid={dataTestId}
        />

        <span className={styles.icon} aria-hidden="true">
          {isLoading ? (
            <span className={styles.spinner} />
          ) : (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path
                d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7Zm0 9.5a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5Z"
                fill="currentColor"
              />
            </svg>
          )}
        </span>
      </div>

      {showDropdown && (
        // NOSONAR S6819 — native <datalist> cannot show async loading state or match
        // the styled Melissa suggestion dropdown; this WAI-ARIA combobox/listbox is
        // intentional (keyboard nav, aria-activedescendant, focus management).
        <ul // NOSONAR S6819 — see rationale above
          ref={listRef}
          id={`${inputId}-listbox`}
          role="listbox" // NOSONAR
          aria-label={listboxAriaLabel}
          className={styles.dropdown}
        >
          {dropdownListContent}
        </ul>
      )}

      {error && (
        <span className={styles.error} role="alert">
          {error}
        </span>
      )}
    </div>
  );
}

export default AddressAutocomplete;
